import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../lib/session";
import { listMembers, addMember, updateMember, removeMember,
         memberInvites, sendInvite, handicapHistory } from "../lib/api";
import { Field, Notice, Empty, PageHead, Loading } from "../components/ui";
import Back from "../components/Back";
import CopyBox from "../components/CopyBox";
import HandicapChart from "../components/HandicapChart";

/**
 * The roster.
 *
 * Members are people whether or not they ever sign in. The organiser adds
 * them, enters their cards, and if they later sign in with a matching
 * email they inherit their own history.
 *
 * Everything about a member is editable by an organiser — a handicap that
 * can't be corrected is worse than no handicap at all.
 */

const blankForm = { full_name: "", email: "", handicap_index: "", plays_from: "male",
                    role: "player" };

function MemberForm({ initial, onSave, onCancel, onDelete, busy, isSelf, canSetRole }) {
  const [f, setF] = useState(initial);
  return (
    <form className="surface" style={{ padding: "1.1rem", marginBottom: "1rem" }}
          onSubmit={(e) => { e.preventDefault(); onSave(f, {}); }}>
      <Field label="Name">
        <input className="input" required autoFocus value={f.full_name}
               onChange={(e) => setF({ ...f, full_name: e.target.value })}
               placeholder="Dave Prentice" />
      </Field>
      <Field label="Email"
             hint="Optional. Needed only if they want their own login — and nothing is sent unless you ask for it.">
        <input className="input" type="email" value={f.email ?? ""}
               autoCapitalize="off" spellCheck="false"
               onChange={(e) => setF({ ...f, email: e.target.value })}
               placeholder="dave@example.com" />
      </Field>
      <div className="grid2">
        <Field label="Handicap index" hint="e.g. 12.4. Blank if unknown.">
          <input className="input" inputMode="decimal" value={f.handicap_index ?? ""}
                 onChange={(e) => setF({ ...f, handicap_index: e.target.value })}
                 placeholder="12.4" />
        </Field>
        <Field label="Plays from" hint="Which tees they normally use.">
          <select className="select" value={f.plays_from ?? "male"}
                  onChange={(e) => setF({ ...f, plays_from: e.target.value })}>
            <option value="male">Men's tees</option>
            <option value="female">Ladies' tees</option>
            <option value="any">Either</option>
          </select>
        </Field>
      </div>
      {canSetRole ? (
        <Field label="Role"
               hint="Player enters their own card. Organiser runs the golf — courses, competitions, anyone's card. Admin also decides who is what.">
          <select className="select" value={f.role} disabled={isSelf}
                  onChange={(e) => setF({ ...f, role: e.target.value })}>
            <option value="player">Player</option>
            <option value="organiser">Organiser</option>
            <option value="admin">Admin</option>
          </select>
          {isSelf && <small>You can't change your own role.</small>}
        </Field>
      ) : f.role && f.role !== "player" ? (
        <Field label="Role">
          <div className="row" style={{ padding: ".3rem 0" }}>
            <span className="pill pill-mark">{f.role}</span>
            <span className="muted" style={{ fontSize: ".75rem" }}>
              Only an admin can change this.
            </span>
          </div>
        </Field>
      ) : null}
      <div className="row" style={{ marginTop: ".4rem", flexWrap: "wrap", gap: ".5rem" }}>
        <button className="btn btn-primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        {/* Only offered when there's an address to send to, and only for
            someone who hasn't already got an account. */}
        {(f.email ?? "").trim() && !f.has_account && (
          <button type="button" className="btn btn-dark" disabled={busy}
                  onClick={() => onSave(f, { invite: true })}>
            {busy ? "Saving…" : "Save and invite"}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <span className="spacer" />
        {onDelete && !isSelf && (
          <button type="button" className="btn-danger" onClick={onDelete}>
            Remove from society
          </button>
        )}
      </div>
    </form>
  );
}

export default function Members() {
  const { society, profile, isOrganiser, isAdmin, refresh } = useSession();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(null);   // whoever was added last, to tell them about
  const [invites, setInvites] = useState({}); // profile_id -> invite state
  const [sending, setSending] = useState(null);
  const [sentTo, setSentTo] = useState("");
  const [history, setHistory] = useState(null);

  const loadInvites = () =>
    memberInvites(society.id)
      .then((rows) => setInvites(Object.fromEntries(rows.map((r) => [r.profile_id, r]))))
      .catch(() => {});   // the roster still works if this view is missing

  const load = () =>
    listMembers(society.id)
      .then((d) => setRows(d.sort((a, b) =>
        a.profiles.full_name.localeCompare(b.profiles.full_name))))
      .catch((e) => setError(e.message));

  useEffect(() => {
    // together, or the badge shows "not invited" before the invite data
    // arrives and then corrects itself
    Promise.all([load(), loadInvites()]);
    /* eslint-disable-next-line */
  }, [society.id]);

  async function invite(profileId) {
    setError(""); setSentTo(""); setSending(profileId);
    try {
      const res = await sendInvite(society.id, profileId);
      setSentTo(`Invitation emailed to ${res.sent_to}.`);
      await loadInvites();
    } catch (e) { setError(e.message); }
    finally { setSending(null); }
  }

  async function create(f, opts) {
    setError(""); setBusy(true);
    try {
      const created = await addMember(society.id, f);
      setAdding(false);
      setAdded(f.email?.trim()
        ? { name: f.full_name.trim(), email: f.email.trim(), profile_id: created.id }
        : null);

      /* Saving and inviting are one action for the organiser but two for
         the database. If the send fails — the function isn't deployed, the
         domain isn't verified — the member is still saved, and the message
         has to say so or it reads as though nothing happened. */
      if (opts?.invite && f.email?.trim()) {
        try {
          const res = await sendInvite(society.id, created.id);
          setSentTo(`${f.full_name.trim()} added, and invited at ${res.sent_to}.`);
        } catch (e) {
          setError(`${f.full_name.trim()} was added, but the invitation didn't send: ${e.message}`);
        }
      }
      await load();
      await loadInvites();
    }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function save(f, opts) {
    setError(""); setBusy(true);
    try {
      await updateMember(society.id, editing.profiles.id, {
        full_name: f.full_name.trim(),
        email: f.email?.trim() || null,
        handicap_index: f.handicap_index,
        plays_from: f.plays_from,
        role: f.role && f.role !== editing.role ? f.role : null });

      /* Saving and inviting are one action for the organiser but two for
         the database. If the send fails, the member is still saved — the
         message has to say so, or it reads as though nothing happened. */
      if (opts?.invite && f.email?.trim()) {
        try {
          const res = await sendInvite(society.id, editing.profiles.id);
          setSentTo(`Saved, and invited at ${res.sent_to}.`);
        } catch (e) {
          setError(`Saved, but the invitation didn't send: ${e.message}`);
        }
      }

      setEditing(null);
      await load();
      await loadInvites();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function drop() {
    if (!window.confirm(`Remove ${editing.profiles.full_name} from ${society.name}?`)) return;
    setError(""); setBusy(true);
    try { await removeMember(society.id, editing.profiles.id); setEditing(null); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (!rows) return <Loading />;

  const joined = rows.filter((r) => r.profiles.user_id).length;
  const noIndex = rows.filter((r) => r.profiles.handicap_index == null).length;

  return (
    <>
      <Back to="/society">Society</Back>
      <PageHead title="Members"
                sub={`${rows.length} in ${society.name} · ${joined} signed in`}>
        {isOrganiser && !adding && !editing && (
          <button className="btn btn-primary" onClick={() => setAdding(true)}>Add member</button>
        )}
      </PageHead>

      <Notice kind="error">{error}</Notice>

      {added && (
        <div className="surface" style={{ padding: "1rem 1.1rem", marginBottom: "1.25rem",
                                          borderLeft: "3px solid var(--red)" }}>
          <div className="row" style={{ marginBottom: ".5rem" }}>
            <strong style={{ fontSize: ".9375rem" }}>{added.name} is on the roster</strong>
            <span className="spacer" />
            <button className="btn-link" onClick={() => setAdded(null)}>Dismiss</button>
          </div>
          <p className="muted" style={{ fontSize: ".8125rem", lineHeight: 1.6, margin: "0 0 .8rem" }}>
            They can play and have cards entered for them straight away. Email
            them their way in, or send the message yourself.
          </p>
          <div className="row" style={{ marginBottom: ".9rem", flexWrap: "wrap", gap: ".5rem" }}>
            <button className="btn btn-primary" disabled={sending === added.profile_id}
                    onClick={() => invite(added.profile_id)}>
              {sending === added.profile_id ? "Sending…" : `Email ${added.email}`}
            </button>
          </div>
          <Notice kind="ok">{sentTo}</Notice>
          <CopyBox text={
`You're set up on PostcardGolf for ${society.name}.

Go to postcardgolf.app and choose "New here? Create an account".
Use ${added.email} — it's the address we have for you.

Society code: ${society.join_code}

You'll see the fixtures and can enter your own cards.`} />
        </div>
      )}

      {!adding && !editing && isAdmin && (
        <p className="muted" style={{ fontSize: ".75rem", marginBottom: "1rem", lineHeight: 1.6 }}>
          The society code, invitations and who may sign up are on{" "}
          <Link to="/society" style={{ color: "var(--red)", fontWeight: 600 }}>
            society settings
          </Link>.
        </p>
      )}

      {noIndex > 0 && isOrganiser && !adding && !editing && (
        <div className="notice notice-info" style={{ marginBottom: "1rem" }}>
          {noIndex} member{noIndex === 1 ? " has" : "s have"} no handicap index.
          They're skipped when a competition enters the society — add one, then
          press "Enter everyone" on the competition.
        </div>
      )}

      {adding && (
        <MemberForm initial={blankForm} busy={busy} canSetRole={isAdmin}
                    onSave={create} onCancel={() => setAdding(false)} />
      )}

      {editing && editing.profiles.email && !editing.profiles.user_id && (
        <div className="surface" style={{ padding: ".9rem 1.1rem", marginBottom: ".7rem" }}>
          <div className="row" style={{ flexWrap: "wrap", gap: ".6rem" }}>
            <span style={{ fontSize: ".8125rem" }}>
              {invites[editing.profiles.id]?.invite_sent_at
                ? `Emailed ${new Date(invites[editing.profiles.id].invite_sent_at)
                    .toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                : "Not invited yet"}
            </span>
            <span className="spacer" />
            <button className="btn btn-ghost" disabled={sending === editing.profiles.id}
                    onClick={() => invite(editing.profiles.id)}>
              {sending === editing.profiles.id ? "Sending…"
                : invites[editing.profiles.id]?.invite_sent_at ? "Send again" : "Email invitation"}
            </button>
          </div>
          <Notice kind="ok">{sentTo}</Notice>
        </div>
      )}

      {editing && history && history.length > 0 && (
        <div className="surface" style={{ padding: "1rem 1.1rem", marginBottom: ".7rem" }}>
          <span className="eyebrow">Handicap index over time</span>
          <div style={{ marginTop: ".5rem" }}>
            <HandicapChart history={history} />
          </div>
        </div>
      )}

      {editing && (
        <MemberForm
          initial={{
            full_name: editing.profiles.full_name,
            email: editing.profiles.email ?? "",
            handicap_index: editing.profiles.handicap_index ?? "",
            plays_from: editing.profiles.plays_from ?? "male",
            role: editing.role,
            has_account: !!editing.profiles.user_id }}
          busy={busy}
          isSelf={editing.profiles.id === profile?.id}
          canSetRole={isAdmin}
          onSave={save}
          onCancel={() => setEditing(null)}
          onDelete={drop} />
      )}

      {rows.length === 0 ? (
        <Empty title="No members yet"
               action={isOrganiser && (
                 <button className="btn btn-primary" onClick={() => setAdding(true)}>
                   Add your first member
                 </button>)}>
          Add everyone who plays, whether or not they'll ever use the app.
          You can enter cards on their behalf.
        </Empty>
      ) : (
        <div className="list" style={{ "--cols": "minmax(0,1fr) 3.5rem 5rem 4.5rem" }}>
          <div className="list-row head">
            <span>Name</span><span style={{ textAlign: "right" }}>Index</span>
            <span>Tees</span><span>Status</span>
          </div>
          {rows.map((r) => {
            const p = r.profiles;
            const row = (
              <>
                <span style={{ minWidth: 0 }}>
                  <strong style={{ display: "block", overflow: "hidden",
                                   textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.full_name}
                  </strong>
                  <small className="muted" style={{ fontSize: ".6875rem" }}>
                    {p.email || "no email"}
                  </small>
                </span>
                <span className="fig" style={{ textAlign: "right", fontSize: "1rem",
                                               color: p.handicap_index == null ? "var(--red)" : undefined }}>
                  {p.handicap_index ?? "—"}
                </span>
                <span className="muted" style={{ fontSize: ".8125rem" }}>
                  {p.plays_from === "female" ? "Ladies'" : p.plays_from === "male" ? "Men's" : "Either"}
                </span>
                <span>
                  {r.role !== "player"
                    ? <span className="pill pill-mark">{r.role}</span>
                    : p.user_id
                      ? <span className="pill pill-quiet">joined</span>
                      : invites[p.id]?.invite_sent_at
                        ? <span className="pill pill-quiet">emailed</span>
                        : p.email
                          ? <span className="pill pill-quiet">not invited</span>
                          : <span className="pill pill-quiet">no email</span>}
                </span>
              </>
            );
            return isOrganiser ? (
              <button key={p.id} className={`list-row${p.id === profile?.id ? " you" : ""}`}
                      onClick={() => {
                        setAdding(false); setEditing(r); setHistory(null);
                        handicapHistory(r.profiles.id).then(setHistory).catch(() => setHistory([]));
                      }}
>
                {row}
              </button>
            ) : (
              <div key={p.id} className={`list-row${p.id === profile?.id ? " you" : ""}`}
                   >
                {row}
              </div>
            );
          })}
        </div>
      )}

      {isOrganiser && rows.length > 0 && !editing && !adding && (
        <p className="muted" style={{ fontSize: ".75rem", marginTop: ".8rem", lineHeight: 1.6 }}>
          Tap anyone to edit them. "Invited" means they haven't signed in yet —
          their scores still count, and they'll inherit them when they do.
        </p>
      )}
    </>
  );
}
